import React, { useEffect, useState, useRef } from "react";
import {fetchOrder, acceptOrderFromAdmin, cancelOrderFromAdmin, updateOrderFromAdmin} from "../service/api/orders";
import { useParams } from "react-router-dom";
import DeliveryPartnerCard from "../components/DeliveryPartner";
import "../css/OrderDetail.css";
import {formatDate} from "../utils/formatTime";
import useTimer from "../hooks/useTimer";
import {PrintIcon, ChevronDown, FileIcon, ManageIcon} from "../utils/icons";

import {
    Check, X,
} from 'lucide-react';
import EditOrderModal from "../components/EditOrderModal";
import DeliveryMap from "../components/DeliveryMap";
import {PrintBill, usePrintBill} from "../components/PrintBill";

const STATUS_MAP = {
    1:  { label: "Placed",       status: "pending"   },
    2:  { label: "Accepted",  status: "pending"   },
    3:  { label: "Assigned",  status: "assigned"  },
    4:  { label: "Picked Up", status: "pickedup"  },
    5:  { label: "Completed", status: "completed" },
    6:  { label: "Cancelled", status: "cancelled" },
    11: { label: "Placed",       status: "pending"   },
    9: {label: "Payment Failed", status: "cancelled"}
};

const CANCEL_REASONS = [
    "Customer requested cancellation",
    "Item out of stock",
    "Restaurant not accepting orders",
    "Delivery not available in this area",
    "Duplicate order",
    "Payment issue",
];

const canCancelOrder = (statusId) => [1, 2, 3, 4, 11].includes(statusId);
const canAcceptOrder = (statusId) => statusId === 1 || statusId === 11;

const Skeleton = ({ w, h, mb }) => (
    <div className="skeleton" style={{ width: w, height: h, marginBottom: mb }} />
);

const Label = ({ children }) => <div className="label">{children}</div>;

const SectionTitle = ({ children }) => (
    <div className="section-title">{children}</div>
);

const Card = ({ children, delay, gradient }) => (
    <section className={`card${gradient ? " card--gradient" : ""}`} data-delay={delay}>
        {children}
    </section>
);

const PriceSummary = ({ order }) => {
    if (!order) return null;

    const subTotal = Number(order?.sub_total || 0);
    const delivery = Number(order?.delivery_charge || 0);

    const serviceFee =
        order?.service_fee_type === "percent"
            ? (subTotal * Number(order?.service_fee || 0)) / 100
            : Number(order?.service_fee || 0);

    return (
        <div className="price-summary">
            <PriceRow label="Subtotal" value={subTotal} />

            <PriceRow label="Delivery Charge" value={delivery} />

            {serviceFee > 0 && (
                <PriceRow
                    label={`Service Fee ${
                        order?.service_fee_type === "percent"
                            ? `(${order?.service_fee}%)`
                            : ""
                    }`}
                    value={serviceFee}
                />
            )}

            {order?.tip_amount && (
                <PriceRow
                    label="Tip Amount"
                    value={Number(order?.tip_amount)}
                    highlight
                />
            )}

            <PriceRow
                label="Total"
                value={Number(order?.total || subTotal + serviceFee + delivery)}
            />


            {order?.payment_mode !== "RAZORPAY" && (<PriceRow
                label="Payable"
                value={Number(order?.total || subTotal + serviceFee + delivery)}
                highlight
            />)}
        </div>
    );
};

const InfoField = ({ label, value, mono, link }) => (
    <div className="info-field">
        <Label>{label}</Label>

        {link ? (
            <a
                href={link}
                className={`value${mono ? " mono" : ""}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                {value || "—"}
            </a>
        ) : (
            <div className={`value${mono ? " mono" : ""}`}>
                {value || "—"}
            </div>
        )}
    </div>
);


const StatBox = ({label, value, variant = "primary"}) => (
    <div className={`stat-box stat-box--${variant}`}>
        <Label>{label}</Label>
        <div className="stat-box__value">{value}</div>
    </div>
);

const getTimingStyle = (totalMinutes, isOngoing) => {
    if (totalMinutes < 30)  return { color: "#22c55e", label: "On Time",  labelBg: "#f0fdf4", labelColor: "#16a34a" };
    if (totalMinutes < 45)  return { color: "#f59e0b", label: "Late",     labelBg: "#fffbeb", labelColor: "#b45309" };
    return                         { color: "#ef4444", label: "Too Late", labelBg: "#fef2f2", labelColor: "#dc2626" };
};

const OrderItem = ({ name, price, quantity }) => (
    <div className="order-item">
        <div className="order-item__left">
            <span className="order-item__qty">{quantity}×</span>
            <span className="order-item__name">{name}</span>
        </div>
        <span className="price-row__value price-row__value--highlight">
          ₹{Number(price)?.toFixed(2)}
        </span>
    </div>
);

const PriceRow = ({label, value, highlight}) => (
    <div className={`price-row${highlight ? " price-row--highlight" : ""}`}>
        <span>{label}</span>
        <span className={`price-row__value${highlight ? " price-row__value--highlight" : ""}`}>
          ₹{Number(value)?.toFixed(2)}
        </span>
    </div>
);

const TimelineItem = ({time, text, isLast}) => (
    <div className={`timeline-item${isLast ? " timeline-item--last" : ""}`}>
        <div className="timeline-track">
            {!isLast && <div className="timeline-line" />}
            <div className="timeline-dot" />
        </div>
        <div className="timeline-content">
            <span className="timeline-time">
                  {time &&
                      new Date(time).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                      })}
            </span>
            <div className="timeline-text">{text}</div>
        </div>
    </div>
);

const OrderStatus = ({ statusId }) => {
    const { label, status } = STATUS_MAP[statusId] ?? { label: "Unknown", status: "pending" };
    return (
        <div className={`order-status order-status--${status}`}>
            <Label>Status</Label>
            <div className="order-status__label">
                <span className="order-status__dot" />
                {label}
            </div>
        </div>
    );
};

const CancelReasonModal = ({ order, refundType, onConfirm, onClose, loading }) => {
    const [selected, setSelected] = useState("");
    const [custom,   setCustom]   = useState("");

    const isOther     = selected === "other";
    const finalReason = isOther ? custom.trim() : selected;
    const canSubmit   = finalReason.length >= 3 && !loading;

    const total     = parseFloat(order?.total ?? 0);
    const walletAmt = parseFloat(order?.wallet_amount ?? 0);

    const refundLabel = {
        NOREFUND: walletAmt > 0 ? `₹${walletAmt.toFixed(2)} wallet only` : "NO REFUND",
        HALF:     `₹${(total / 2).toFixed(2)} half refund`,
        FULL:     `₹${total.toFixed(2)} full refund`,
    }[refundType] ?? "NO REFUND";

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="modal-header">
                    <div>
                        <h3 className="modal-title">Cancel the order with {refundLabel}?</h3>
                    </div>
                    <button className="modal-close" onClick={onClose} disabled={loading}>✕</button>
                </div>

                {/* Body */}
                <div className="modal-body">
                    <div className="modal-section-label">Select Reason for Cancellation</div>

                    <div className="modal-reasons">
                        {CANCEL_REASONS.map((r) => (
                            <button
                                key={r}
                                className={`modal-reason-chip${selected === r ? " is-active" : ""}`}
                                onClick={() => setSelected(r)}
                            >
                                {r}
                            </button>
                        ))}
                        <button
                            className={`modal-reason-chip${isOther ? " is-active" : ""}`}
                            onClick={() => setSelected("other")}
                        >
                            Other…
                        </button>
                    </div>

                    {isOther && (
                        <textarea
                            className="modal-textarea"
                            placeholder="Describe the reason…"
                            value={custom}
                            onChange={(e) => setCustom(e.target.value)}
                            rows={2}
                            autoFocus
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <button className="btn" onClick={onClose} disabled={loading}>Keep Order</button>
                    <button
                        className="action-btn action-btn--danger"
                        disabled={!canSubmit}
                        onClick={() => onConfirm(finalReason)}
                    >
                        {loading ? "Cancelling…" : "Confirm Cancel"}
                    </button>
                </div>

            </div>
        </div>
    );
};

const OrderActionsCard = ({ order, onOrderUpdate, showToast, setLoading, onAccept, accepting  }) => {
    const [dropOpen,   setDropOpen]   = useState(false);
    const [refundType, setRefundType] = useState(null);
    const [showModal,  setShowModal]  = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const dropRef = useRef(null);

    const canAccept = canAcceptOrder(order?.orderstatus_id);

    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const total= parseFloat(order?.total ?? 0);

    const handleRefundPick = (type) => {
        setRefundType(type);
        setDropOpen(false);
        setShowModal(true);
    };

    const handleConfirm = async (reason) => {
        setCancelling(true);
        setLoading("Order Cancelling...");
        try {
            const res = await cancelOrderFromAdmin(order.id, reason, refundType);
            await onOrderUpdate?.();
            showToast("success", res?.message || "Success");
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";
            showToast("error", message);
        } finally {
            setShowModal(false);
            setCancelling(false);
        }
    };

    if (!canCancelOrder(order?.orderstatus_id)) return null;

    return (
        <>
            <Card delay="3">
                <Label>Order Actions</Label>


                <div className="oa-wrap" ref={dropRef}>
                    {canAccept && (
                        <button className="action-btn action-btn--accept" disabled={accepting} onClick={onAccept}>
                            {accepting ? "Accepting…" : "✓ Accept Order"}
                        </button>
                    )}
                    <button
                        className={`oa-trigger${dropOpen ? " is-open" : ""}`}
                        onClick={() => setDropOpen((v) => !v)}
                    >
                        <span className="oa-trigger-x">✕</span>
                            Cancel Order
                        <ChevronDown open={dropOpen} />
                    </button>

                    {dropOpen && (
                        <div className="oa-dropdown">
                            <div className="oa-dropdown-label">Choose refund type</div>

                            <button className="oa-refund-row" onClick={() => handleRefundPick("NOREFUND")}>
                                <div className="oa-refund-row__info">
                                    <span className="oa-refund-row__name">No Refund</span>
                                </div>
                                <span className="oa-refund-row__amt">₹0</span>
                            </button>

                            <div className="oa-sep" />

                            <button className="oa-refund-row" onClick={() => handleRefundPick("HALF")}>
                                <div className="oa-refund-row__info">
                                    <span className="oa-refund-row__name">Half Refund</span>
                                    <span className="oa-refund-row__sub">50% of order total</span>
                                </div>
                                <span className="oa-refund-row__amt oa-refund-row__amt--green">
                                    ₹{(total / 2).toFixed(2)}
                                </span>
                            </button>

                            <div className="oa-sep" />

                            <button className="oa-refund-row" onClick={() => handleRefundPick("FULL")}>
                                <div className="oa-refund-row__info">
                                    <span className="oa-refund-row__name">Full Refund</span>
                                    <span className="oa-refund-row__sub">Complete order amount</span>
                                </div>
                                <span className="oa-refund-row__amt oa-refund-row__amt--green">
                                    ₹{total.toFixed(2)}
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </Card>

            {showModal && (
                <CancelReasonModal
                    order={order}
                    refundType={refundType}
                    onConfirm={handleConfirm}
                    onClose={() => !cancelling && setShowModal(false)}
                    loading={cancelling}
                />
            )}
        </>
    );
};

const PageHeader = ({ order, loading, setModalOpen, triggerPrint, triggerThermal}) => {

    return (
        <header className="page-header">
            <div>
                <p className="page-header__eyebrow">Order Details</p>
                <h1 className="page-header__title">
                    {loading ? <Skeleton w={180} h={22} /> : (order?.unique_order_id || "—")}
                </h1>
            </div>
            <div className="page-header__actions">
                {[1, 2, 3, 8, 10, 11].includes(order?.orderstatus_id) && (
                    <button className="btn" onClick={() => setModalOpen("MANAGE_ORDERS")}>
                        <ManageIcon /> Manage Order
                    </button>
                )}
                <button className="btn" onClick={triggerPrint}><PrintIcon/> Print Bill</button>
                <button className="btn" onClick={triggerThermal}><FileIcon/> Thermal Bill</button>
            </div>
        </header>
    );
};

function StatusBadge({status }) {
    const online = status === 1;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11, fontWeight: 600, letterSpacing: "0.03em",
            color:      online ? "#059669" : "#EF4444",
            flexShrink: 0,
        }}>
          <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: online ? "#10B981" : "#EF4444",
              display: "inline-block",
          }} />
            {online ? "Online" : "Offline"}
        </span>
    );
}


const OrderPage = () => {
    const { orderId } = useParams();

    const { printRef, triggerPrint, thermalRef, triggerThermal  } = usePrintBill();

    const [data, setData]  = useState(null);
    const [loading,   setLoading]   = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [toast,     setToast]     = useState(null);

    const [modalOpen, setModalOpen] = useState(null);

    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    };

    const loadOrder = async () => {
        setLoading("Loading Order...");
        try {
            const response = await fetchOrder(orderId);
            setData(response?.data);
        } catch {
            showToast("error", "Failed to load order.");
        } finally {
            setLoading(null);
        }
    };

    const handleAccept = async () => {
        setAccepting(true);
        setLoading("Accepting Order...");
        try {
            const res = await acceptOrderFromAdmin(order?.id);
            await loadOrder();
            showToast("success", res?.message || "Success");
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";
            showToast("error", message);
        } finally {
            setAccepting(false);
        }
    };


    const handleEdit = async (request) => {
        setLoading("Updating Order...");
        try {
            const res = await updateOrderFromAdmin(order?.id, request);
            await loadOrder();
            showToast("success", res?.message || "Success");
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";
            showToast("error", message);
        } finally {
            setAccepting(false);
        }
    };

    useEffect(() => { loadOrder(); }, [orderId]);

    const order = data?.order;

    const { days, hours, minutes, seconds, isOngoing } = useTimer(
        order?.created_at,
        order?.orderstatus_id,
        order?.updated_at
    );

    const totalMinutes  = (days * 1440) + (hours * 60) + minutes;
    const timingStyle   = getTimingStyle(totalMinutes, isOngoing);
    const activities = data?.activities;


    return (
        <div className="order-page">
            <PageHeader order={order} loading={loading} setModalOpen={(l)=>setModalOpen(l)} triggerPrint={triggerPrint} triggerThermal={triggerThermal}/>

            <div className="order-detail-grid">
                {loading ? (
                    <Card delay="1">
                        <div className="skeleton-group">
                            <Skeleton h={22} w="55%" mb={4}/>
                            <Skeleton h={14} w="35%"/>
                        </div>
                    </Card>
                ) : (
                    <Card delay="1">
                        <div className="card-header">
                            <div className="card-header__left">
                                <h2>{order?.restaurant?.name}</h2>
                                <span className="card-header__id">{order?.unique_order_id}</span>
                            </div>
                            <div className="card-header__right">
                                <Label>Order Date</Label>
                                <div className="card-header__date">
                                    {order?.created_at && formatDate(order?.created_at)}
                                </div>
                            </div>
                        </div>

                        <div className={"divider"}/>

                        <div className="order-main-row">
                        <div className="order-customer">
                                <SectionTitle>Customer Details</SectionTitle>
                                <InfoField label="Name" value={order?.user?.name}/>
                                <InfoField label="Email"            value={order?.user?.email} />
                                <InfoField label="Contact"          value={order?.user?.phone} />
                                <InfoField label="Delivery Address" value={order?.address} />
                            </div>
                            <div className="order-status-col">
                                <OrderStatus statusId={order?.orderstatus_id} />
                                <div className="stat-boxes">
                                    <StatBox label="Order Type" value={order?.delivery_type === 1 ? "Delivery" : "Self Pickup"} variant="accent" />
                                    <StatBox label="Payment" value={order?.payment_mode} variant="primary" />
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: 20 }}>
                            <SectionTitle>Order Items</SectionTitle>
                            {order?.orderitems?.map((item, idx) => (
                                <OrderItem key={idx} name={item?.name} price={item?.price} quantity={item?.quantity} />
                            ))}
                        </div>

                        <PriceSummary order={order} />
                    </Card>
                )}

                <div>
                    {order?.payment_mode === "RAZORPAY" && (
                        <Card delay="2">
                            <SectionTitle>Razorpay Details</SectionTitle>
                            <InfoField
                                label="Order ID"
                                value={order?.razorpay_data?.razorpay_order_id_first} mono
                                link={`https://dashboard.razorpay.com/app/orders/${order?.razorpay_data?.razorpay_order_id_first}`}
                            />
                            <InfoField
                                label="Payment ID"
                                value={order?.razorpay_data?.razorpay_payment_id}
                                mono
                                link={`https://dashboard.razorpay.com/app/payments/${order?.razorpay_data?.razorpay_payment_id}`}
                            />
                        </Card>
                    )}

                    {order?.orderstatus_id === 6 && order?.cancel_reason && (
                        <Card delay="3">
                            <Label>Cancel Reason</Label>
                            <div className="distance-value" style={{ color: "#ef4444", fontSize: "0.82rem" }}>
                                {order?.cancel_reason}
                            </div>
                        </Card>
                    )}

                    <Card delay="4">
                        <Label>Distance</Label>
                        <div className="distance-value">
                            {order?.distance ? parseFloat(order?.distance).toFixed(2) : "—"}
                            <span className="distance-unit">km from store</span>
                        </div>
                    </Card>

                    <OrderActionsCard
                        order={order}
                        onOrderUpdate={loadOrder}
                        setLoading={setLoading}
                        showToast={showToast}
                        onAccept={handleAccept}
                        accepting={accepting}
                    />

                    {(order?.accept_delivery) && (
                        <Card delay="4">
                            <div className="distance-value">
                                {order?.accept_delivery?.user?.name}
                                <span className="distance-unit">Assigned Guy</span>
                            </div>
                            <StatusBadge status={order?.accept_delivery?.user?.delivery_guy_detail?.status} />
                        </Card>
                    )}

                    {(canCancelOrder(order?.orderstatus_id) && order?.delivery_type === 1) && (
                        <DeliveryPartnerCard
                            order={order}
                            showToast={showToast}
                            onOrderUpdate={loadOrder}
                            setPageLoading={setLoading}
                        />
                    )}
                </div>

                <div>
                    <Card delay="2">
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Label>{isOngoing ? "Ongoing" : "Completed In"}</Label>

                            {timingStyle.label && (
                                <span className="completion-time__label" style={{
                                    background: timingStyle.labelBg,
                                    color: timingStyle.labelColor,
                                    border: `1px solid ${timingStyle.labelColor}33`,
                                }}>
                                {timingStyle.label}
                            </span>
                            )}
                        </div>

                        <div className="completion-time">
                            {days > 0 && (
                                <>
                                    <span className="completion-time__num" style={{ color: timingStyle.color }}>{days}</span>
                                    <span className="completion-time__unit">day{days !== 1 ? "s" : ""}</span>
                                </>
                            )}
                            {(days > 0 || hours > 0) && (
                                <>
                                    <span className="completion-time__num" style={{ color: timingStyle.color }}>{String(hours).padStart(2, "0")}</span>
                                    <span className="completion-time__unit">hr</span>
                                </>
                            )}
                            <span className="completion-time__num" style={{ color: timingStyle.color }}>{String(minutes).padStart(2, "0")}</span>
                            <span className="completion-time__unit">min</span>
                            <span className="completion-time__num" style={{ color: timingStyle.color }}>{String(seconds).padStart(2, "0")}</span>
                            <span className="completion-time__unit">sec</span>
                        </div>
                    </Card>

                    <Card delay="3">
                        <SectionTitle>Activity Log</SectionTitle>
                        {activities?.length > 0
                            ? [...activities].reverse().map((item, idx) => (
                                <TimelineItem
                                    key={idx}
                                    time={item?.created_at}
                                    text={`${item?.description} by ${item?.causer?.name}`}
                                    isLast={idx === activities.length - 1}
                                />
                            ))
                            : <p className="timeline-empty">No activity recorded.</p>
                        }
                    </Card>

                    <Card delay="4">
                        <Label>Delivery Route</Label>
                        <DeliveryMap order={order} />
                    </Card>
                </div>
            </div>

            {loading && (
                <div className="loading-overlay">
                    <div className="loading-overlay__box">
                        <div className="spinner"/>
                        <span className="loading-overlay__text">{loading}</span>
                    </div>
                </div>
            )}
            {toast && (
                <div className={`toast toast--${toast?.type}`}>
                    {toast?.type === 'success' ? (
                            <Check className="toast__icon" size={18} />
                        ) : (
                            <X className="toast__icon" size={18} />
                        )
                    }
                    {toast?.message}
                </div>
            )}

            {modalOpen && (
                <EditOrderModal
                    order={data?.order}
                    onSave={handleEdit}
                    onClose={()=> setModalOpen(null)}
                    existingItems={data?.order?.orderitems}
                    showToast={showToast}
                />
            )}

            <PrintBill ref={printRef} thermalRef={thermalRef} order={order} />
        </div>
    );
};

export default OrderPage;